import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { TaxRate } from '../types';

interface TaxRateMatrixProps {
  taxRates: TaxRate[];
  onRateClick?: (rate: TaxRate) => void;
}

const TaxRateMatrix = ({ taxRates, onRateClick }: TaxRateMatrixProps) => {
  const [sortBy, setSortBy] = useState<'uf' | 'rate'>('uf');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const sortedRates = [...taxRates].sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    if (sortBy === 'uf') {
      return a.uf.localeCompare(b.uf) * multiplier;
    }
    return (a.rate - b.rate) * multiplier;
  });

  const handleSort = (column: 'uf' | 'rate') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getRateColor = (rate: number): string => {
    if (rate === 0) return 'text-success';
    if (rate < 10) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Matriz de Alíquotas por UF</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Ordenar por:</span>
          <button
            onClick={() => handleSort('uf')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              sortBy === 'uf' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            UF {sortBy === 'uf' && <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} className="inline ml-1" />}
          </button>
          <button
            onClick={() => handleSort('rate')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              sortBy === 'rate' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Alíquota {sortBy === 'rate' && <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} className="inline ml-1" />}
          </button>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">UF</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">CFOP</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">CST</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Alíquota</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Vigência</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedRates.map((rate, index) => (
                <tr
                  key={`${rate.uf}-${rate.cfop}-${index}`}
                  onClick={() => onRateClick?.(rate)}
                  className="hover:bg-muted/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <span className="font-semibold text-foreground">{rate.uf}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-muted-foreground">{rate.cfop}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-muted-foreground">{rate.cst}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-semibold font-mono ${getRateColor(rate.rate)}`}>
                      {rate.rate.toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="text-foreground">
                        {new Date(rate.effectiveDate).toLocaleDateString('pt-BR')}
                      </div>
                      {rate.expirationDate && (
                        <div className="text-muted-foreground text-xs">
                          até {new Date(rate.expirationDate).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {rate.exemptionConditions && rate.exemptionConditions.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                        <Icon name="CheckCircle2" size={12} />
                        Isenção
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        <Icon name="Circle" size={12} />
                        Normal
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-success"></div>
          <span className="text-muted-foreground">Isento (0%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-warning"></div>
          <span className="text-muted-foreground">Reduzido (&lt;10%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-error"></div>
          <span className="text-muted-foreground">Normal (≥10%)</span>
        </div>
      </div>
    </div>
  );
};

export default TaxRateMatrix;